import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import dayjs from "dayjs";
import { ModalPopup } from "_components";
import { alertActions } from "_store";
import { useDispatch } from "react-redux";
import { Delete, Deletewhite } from "images";
import { ERR_MSG } from "constants/labels";
import AgencyEdit from "../AgencyEdit";
import { DEFAULT_FIELD_MAPPING } from "../../utils";

const ManageAgencyTable = ({
  rawTableData = [],
  handleRefresh,
  updateApi,
  deleteApi,
  header = "Manage Agency",
  emptyMessage = "No agency data found.",
  fieldMapping: fieldMappingOverride,
  portalID,
  deleteMessage = "Are you sure you want to delete this item?",
  deleteMessageBulk = "Are you sure you want to delete the selected items?"
}) => {
  const dispatch = useDispatch();
  const fm = { ...DEFAULT_FIELD_MAPPING, ...fieldMappingOverride };

  const [editAnchorEl, setEditAnchorEl] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);

  // Row selection state
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);

  const handleEditClick = (event, agencyData) => {
    setEditAnchorEl(event.currentTarget);
    setSelectedAgency(agencyData);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
    setEditAnchorEl(null);
    setSelectedAgency(null);
  };

  const handleDeleteClick = (agencyData) => {
    setSelectedRowForDelete(agencyData);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDeleteModalOpen(true);
  };

  const handleDeleteAgency = async () => {
    if (!selectedRowForDelete) return;

    try {
      dispatch(alertActions.clear());

      const payload = {
        [fm.deleteIdsKey]: [selectedRowForDelete[fm.id]],
        PortalID: portalID,
      };

      const result = await dispatch(deleteApi(payload)).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message || ERR_MSG["073"],
            header,
          })
        );
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Deleted successfully.",
          header,
          showAfterRedirect: true,
        })
      );

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
    } finally {
      setRowSelection({});
      setSelectedRows([]);
      setIsDeleteModalOpen(false);
      setSelectedRowForDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    try {
      dispatch(alertActions.clear());

      const payload = {
        [fm.deleteIdsKey]: selectedRows.map((row) => row[fm.id]),
        PortalID: selectedRows[0]?.PortalID || 0,
      };

      const result = await dispatch(deleteApi(payload)).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message || ERR_MSG["073"],
            header,
          }),
        );
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Deleted successfully.",
          header,
          showAfterRedirect: true,
        }),
      );

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        }),
      );
    } finally {
      // Reset selections
      setRowSelection({});
      setSelectedRows([]);
      setIsBulkDeleteModalOpen(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: fm.name,
        header: fm.nameLabel,
        size: 200,
        filterFn: (row, columnId, filterValue) =>
          row
            .getValue(columnId)
            ?.toLowerCase()
            .includes(filterValue.toLowerCase()),
      },
      {
        accessorKey: fm.description,
        header: fm.descriptionLabel,
        size: 300,
        filterFn: (row, columnId, filterValue) =>
          row
            .getValue(columnId)
            ?.toLowerCase()
            .includes(filterValue.toLowerCase()),
      },
      {
        accessorKey: fm.timestamp,
        header: fm.timestampLabel,
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? dayjs(date).format("MM/DD/YYYY hh:mm A") : "N/A";
        },
        filterFn: (row, columnId, filterValue) => {
          const rawDate = row.getValue(columnId);
          const formattedDate = rawDate
            ? dayjs(rawDate).format("MM/DD/YYYY hh:mm A")
            : "N/A";
          return formattedDate
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Tooltip title="Edit">
              <IconButton
                color="primary"
                onClick={(e) => handleEditClick(e, row.original)}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => handleDeleteClick(row.original)}
                size="small"
              >
                <img
                  src={Delete}
                  alt="Delete"
                  style={{ width: 20, height: 20 }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [fm]
  );

  const renderTopToolbar = () => (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        padding: "8px",
        flexWrap: "wrap",
      }}
    >
      <Tooltip title="Delete Selected" className="DeleteSelected">
        <div>
          <IconButton
            className="delete"
            onClick={handleBulkDeleteClick}
            disabled={selectedRows.length === 0}
          >
            <img src={Deletewhite} alt="Delete" />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );

  const table = useMaterialReactTable({
    columns,
    data: rawTableData,
    enableExpanding: false,
    enablePagination: true,
    enableColumnFilters: true,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableFilters: true,
    enableColumnFilterModes: false,
    columnFilterDisplayMode: "popover",
    enableHiding: false,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getRowId: (row) => row[fm.id]?.toString(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection: rowSelection,
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#0F3557",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
      },
    },
    muiTableBodyCellProps: {
      sx: { textAlign: "center" },
    },
    renderTopToolbarCustomActions: renderTopToolbar,
    renderEmptyRowsFallback: () => (
      <Typography sx={{ p: 3, textAlign: "center", fontStyle: "italic" }}>
        {emptyMessage}
      </Typography>
    ),
  });

  // Update selected rows when row selection changes
  useEffect(() => {
    const selectedFlatRows = table.getSelectedRowModel().flatRows;
    setSelectedRows(selectedFlatRows.map((row) => row.original));
  }, [rowSelection]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <MaterialReactTable table={table} />
      </Box>

      {/* Agency Edit Component */}
      <AgencyEdit
        agencyData={selectedAgency}
        agencies={rawTableData}
        anchorEl={editAnchorEl}
        isOpen={isEditOpen}
        onClose={handleEditClose}
        handleRefresh={handleRefresh}
        updateApi={updateApi}
        header={header}
        fieldMapping={fieldMappingOverride}
        portalID={portalID}
      />

      {/* Delete Single Modal */}
      {isDeleteModalOpen && (
        <ModalPopup
          header="Delete"
          message1={deleteMessage}
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleDeleteAgency}
          handleSecondaryClick={() => {
            setIsDeleteModalOpen(false);
            setSelectedRowForDelete(null);
          }}
        />
      )}

      {/* Bulk Delete Modal */}
      {isBulkDeleteModalOpen && (
        <ModalPopup
          header="Delete Selected"
          message1={deleteMessageBulk}
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleBulkDelete}
          handleSecondaryClick={() => {
            setIsBulkDeleteModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ManageAgencyTable;